import { useState } from 'react'
import { Form, Typography, Space, Card, notification, Row, Col, Spin } from 'antd'
import { useStoreState, useStoreActions } from 'easy-peasy'
import _ from 'lodash'
import { useAsync } from 'react-use'

import Input from 'components/Input'
import InputNumber from 'components/InputNumber'
import TextArea from 'components/TextArea'
import Select from 'components/Select'
import DatePicker from 'components/DatePicker'
import Upload from 'components/Upload'

import { useTranslate } from 'hooks/translate'

// import { getReportTypeByIdApi } from 'services/reportType'
import { getReportTypeAttributeListApi } from 'services/reportType/attribute'
import { getDictionaryValueFilterListApi } from 'services/filter/dictionary/value'

import numeral from 'utils/numeral'
import { convertDayCodeToTargetDate } from 'utils/date'
import { evil } from 'utils'

import Styled from './styled'

export default function ReportPreview({ id }) {
  const { t } = useTranslate()
  const [form] = Form.useForm()

  const [loading, setLoading] = useState(false)
  // const [reportType, setReportType] = useState({})
  const [reportTypeAttributes, setReportTypeAttributes] = useState([])
  const [allDictionaryValues, setAllDictionaryValues] = useState({})

  const reportStatuses = useStoreState((state) => state.filter.reportStatuses)
  const groupAttributes = useStoreState((state) => state.filter.groupAttributes)
  const dataTypes = useStoreState((state) => state.filter.dataTypes)
  const dictionaries = useStoreState((state) => state.filter.dictionaries)
  const days = useStoreState((state) => state.filter.days)

  const getReportStatuses = useStoreActions((actions) => actions.filter.getReportStatuses)
  const getGroupAttributes = useStoreActions((actions) => actions.filter.getGroupAttributes)
  const getDataTypes = useStoreActions((actions) => actions.filter.getDataTypes)
  const getDictionaries = useStoreActions((actions) => actions.filter.getDictionaries)
  const getDays = useStoreActions((actions) => actions.filter.getDays)

  useAsync(async () => {
    Promise.all([
      getReportStatuses({ forceGet: true }),
      getGroupAttributes({ forceGet: true }),
      getDataTypes({ forceGet: true }),
      getDictionaries({ forceGet: true }),
      getDays({ forceGet: true }),
    ])
  }, [])

  const findCalculationFields = () => {
    return _.filter(reportTypeAttributes, (reportTypeAttribute) => {
      const dataTypeName = _.find(dataTypes, { id: reportTypeAttribute?.data_type_id })?.name
      return dataTypeName === 'calculation'
    })
  }

  const calculationFields = findCalculationFields()

  // const getReportTypeById = async (id) => {
  //   const res = await getReportTypeByIdApi(id)

  //   if (res.status === 'error') {
  //     notification.error({
  //       message: 'Failed',
  //       description: res.message,
  //     })

  //     return
  //   }

  //   setReportType(res?.data)
  // }

  const _list = async () => {
    const params = {
      report_type_id: id,
      is_active: true,
      page: 1,
      size: 100,
    }

    const res = await getReportTypeAttributeListApi(params)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setReportTypeAttributes(res.data?.items)
  }

  useAsync(async () => {
    if (!_.isEmpty(dictionaries)) {
      const results = await _.reduce(
        dictionaries,
        async (result, dictionary) => {
          const res = await getDictionaryValueFilterListApi(dictionary.id)

          return {
            ...(await result),
            [dictionary.id]: res?.data,
          }
        },
        Promise.resolve({})
      )

      setAllDictionaryValues(results)
    }
  }, [dictionaries])

  useAsync(async () => {
    if (!id) {
      return
    }

    setLoading(true)

    // await Promise.all([getReportTypeById(id), _list()])
    await Promise.all([_list()])

    setLoading(false)
  }, [id])

  const renderInputByType = ({ record }) => {
    const dataTypeName = _.find(dataTypes, { id: record?.data_type_id })?.name

    switch (dataTypeName) {
      case 'text': {
        const textProps = {
          placeholder: record?.report_type_attribute_name,
        }

        return (
          <Form.Item
            initialValue={record?.default_value_str}
            label={record?.report_type_attribute_name}
            name={record?.report_type_attribute_code}
            rules={[
              {
                required: !record?.is_nullable,
              },
              {
                type: 'string',
                min: record?.min_length,
                max: record?.max_length,
              },
            ]}
          >
            {record?.row_size > 1 ? (
              <TextArea {...textProps} rows={record?.row_size / 50} />
            ) : (
              <Input {...textProps} />
            )}
          </Form.Item>
        )
      }

      case 'integer': {
        const integerProps = {
          precision: 0,
          style: { width: '100%' },
        }

        return (
          <Form.Item noStyle shouldUpdate={() => true}>
            {({ getFieldValue }) => {
              const getMinQtyValue = () => {
                let result = null

                if (record?.min_qty_type === 'None') {
                } else if (record?.min_qty_type === 'Direct') {
                  result = record?.min_qty_value
                } else if (record?.min_qty_type === 'Attribute') {
                  const reportTypeAttribute = _.find(reportTypeAttributes, {
                    report_type_attribute_id: record?.min_qty_attribute_id,
                  })
                  result = getFieldValue(reportTypeAttribute?.report_type_attribute_code)
                }

                return result
              }

              const getMaxQtyValue = () => {
                let result = null

                if (record?.max_qty_type === 'None') {
                } else if (record?.max_qty_type === 'Direct') {
                  result = record?.max_qty_value
                } else if (record?.max_qty_type === 'Attribute') {
                  const reportTypeAttribute = _.find(reportTypeAttributes, {
                    report_type_attribute_id: record?.max_qty_attribute_id,
                  })
                  result = getFieldValue(reportTypeAttribute?.report_type_attribute_code)
                }

                return result
              }

              const getExtra = (minQtyValue, maxQtyValue) => {
                let result = null

                if (minQtyValue || maxQtyValue) {
                  const minQtyValueFormat = numeral(minQtyValue || 0).format('0,0')
                  const maxQtyValueFormat = numeral(maxQtyValue || 0).format('0,0')
                  result = `(${minQtyValueFormat} - ${maxQtyValueFormat})`
                }

                return result
              }

              const minQtyValue = getMinQtyValue()
              const maxQtyValue = getMaxQtyValue()
              const extra = getExtra(minQtyValue, maxQtyValue)

              return (
                <Form.Item
                  initialValue={record?.default_value_int}
                  label={record?.report_type_attribute_name}
                  name={record?.report_type_attribute_code}
                  rules={[
                    {
                      required: !record?.is_nullable,
                    },
                    {
                      type: 'number',
                      min: minQtyValue,
                      max: maxQtyValue,
                    },
                  ]}
                  extra={extra}
                >
                  <InputNumber {...integerProps} />
                </Form.Item>
              )
            }}
          </Form.Item>
        )
      }

      case 'float': {
        const floatProps = {
          precision: record?.max_decimal,
          style: { width: '100%' },
        }

        return (
          <Form.Item noStyle shouldUpdate={() => true}>
            {({ getFieldValue }) => {
              const getMinQtyValue = () => {
                let result = null

                if (record?.min_qty_type === 'None') {
                } else if (record?.min_qty_type === 'Direct') {
                  result = record?.min_qty_value
                } else if (record?.min_qty_type === 'Attribute') {
                  const reportTypeAttribute = _.find(reportTypeAttributes, {
                    report_type_attribute_id: record?.min_qty_attribute_id,
                  })
                  result = getFieldValue(reportTypeAttribute?.report_type_attribute_code)
                }

                return result
              }

              const getMaxQtyValue = () => {
                let result = null

                if (record?.max_qty_type === 'None') {
                } else if (record?.max_qty_type === 'Direct') {
                  result = record?.max_qty_value
                } else if (record?.max_qty_type === 'Attribute') {
                  const reportTypeAttribute = _.find(reportTypeAttributes, {
                    report_type_attribute_id: record?.max_qty_attribute_id,
                  })
                  result = getFieldValue(reportTypeAttribute?.report_type_attribute_code)
                }

                return result
              }

              const getExtra = (minQtyValue, maxQtyValue) => {
                let result = null

                if (minQtyValue || maxQtyValue) {
                  const minQtyValueFormat = numeral(minQtyValue || 0).format('0,0')
                  const maxQtyValueFormat = numeral(maxQtyValue || 0).format('0,0')
                  result = `(${minQtyValueFormat} - ${maxQtyValueFormat})`
                }

                return result
              }

              const minQtyValue = getMinQtyValue()
              const maxQtyValue = getMaxQtyValue()
              const extra = getExtra(minQtyValue, maxQtyValue)

              return (
                <Form.Item
                  initialValue={record?.default_value_int}
                  label={record?.report_type_attribute_name}
                  name={record?.report_type_attribute_code}
                  rules={[
                    {
                      required: !record?.is_nullable,
                    },
                    {
                      type: 'number',
                      min: minQtyValue,
                      max: maxQtyValue,
                    },
                  ]}
                  extra={extra}
                >
                  <InputNumber {...floatProps} />
                </Form.Item>
              )
            }}
          </Form.Item>
        )
      }

      case 'dictionary': {
        const dictionaryValues = allDictionaryValues?.[record?.dictionary_id]
        return (
          <Form.Item
            initialValue={record?.default_dictionary_value_id}
            label={record?.report_type_attribute_name}
            name={record?.report_type_attribute_code}
            rules={[
              {
                required: !record?.is_nullable,
              },
            ]}
          >
            <Select
              options={_.map(dictionaryValues, (dictionaryValue) => {
                return {
                  label: dictionaryValue?.name,
                  value: dictionaryValue?.id,
                }
              })}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )
      }

      case 'date': {
        return (
          <Form.Item noStyle shouldUpdate={() => true}>
            {({ getFieldValue }) => {
              const getInitialValue = () => {
                let result = null

                if (record?.default_value_type === 'day') {
                  if (record?.default_day_id) {
                    const day = _.find(days, { id: record?.default_day_id })
                    result = convertDayCodeToTargetDate(day?.value_list_code)
                  }
                }

                return result
              }

              const getMinDate = () => {
                let result = null

                if (record?.min_date_type === 'None') {
                } else if (record?.min_date_type === 'day') {
                  if (record?.min_day_id) {
                    const day = _.find(days, { id: record?.min_day_id })
                    result = convertDayCodeToTargetDate(day?.value_list_code)
                  }
                } else if (record?.min_date_type === 'attribute') {
                  if (record?.min_date_attribute_id) {
                    const reportTypeAttribute = _.find(reportTypeAttributes, {
                      report_type_attribute_id: record?.min_date_attribute_id,
                    })
                    result = getFieldValue(reportTypeAttribute?.report_type_attribute_code)
                  }
                }

                return result
              }

              const getMaxDate = () => {
                let result = null

                if (record?.max_date_type === 'None') {
                } else if (record?.max_date_type === 'day') {
                  if (record?.max_day_id) {
                    const day = _.find(days, { id: record?.max_day_id })
                    result = convertDayCodeToTargetDate(day?.value_list_code)
                  }
                } else if (record?.max_date_type === 'attribute') {
                  if (record?.max_date_attribute_id) {
                    const reportTypeAttribute = _.find(reportTypeAttributes, {
                      report_type_attribute_id: record?.max_date_attribute_id,
                    })
                    result = getFieldValue(reportTypeAttribute?.report_type_attribute_code)
                  }
                }

                return result
              }

              const initialValue = getInitialValue()
              const minDate = getMinDate()
              const maxDate = getMaxDate()

              return (
                <Form.Item
                  initialValue={initialValue}
                  label={record?.report_type_attribute_name}
                  name={record?.report_type_attribute_code}
                  rules={[
                    {
                      required: !record?.is_nullable,
                    },
                  ]}
                >
                  <DatePicker
                    disabledDate={(current) => {
                      if (!current) return false

                      if (minDate) {
                        if (current.isBefore(minDate, 'day')) {
                          return true
                        }
                      }

                      if (maxDate) {
                        if (current.isAfter(maxDate, 'day')) {
                          return true
                        }
                      }

                      return false
                    }}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )
            }}
          </Form.Item>
        )
      }

      case 'calculation': {
        return (
          <Form.Item noStyle shouldUpdate={() => false}>
            {({ getFieldValue }) => {
              let initialValue = null

              if (record?.cal_equation) {
                let str = record?.cal_equation

                const codes = str.match(/{([^}]+)}/g)

                _.forEach(codes, (code) => {
                  const data = getFieldValue(code.slice(1,-1)) || 0
                  str = _.replace(str, code, data)
                })

                initialValue = evil(str)
              }

              return (
                <Form.Item
                  initialValue={initialValue}
                  label={record?.report_type_attribute_name}
                  name={record?.report_type_attribute_code}
                >
                  <InputNumber
                    disabled
                    placeholder={record?.report_type_attribute_name}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )
            }}
          </Form.Item>
        )
      }

      case 'attachment': {
        return (
          <Form.Item
            label={record?.report_type_attribute_name}
            name={record?.report_type_attribute_code}
            rules={[
              {
                required: !record?.is_nullable,
              },
              {
                validator: async (rule, value) => {
                  const isFileSizeExceeded = _.some(value, (file) => {
                    return file.size / 1024 / 1024 > record?.max_size
                  })

                  if (isFileSizeExceeded) {
                    return Promise.reject(
                      new Error(`File size must smaller than ${record?.max_size}MB!`)
                    )
                  }

                  return Promise.resolve()
                },
              },
            ]}
          >
            <Upload multiple />
          </Form.Item>
        )
      }

      default: {
        return null
      }
    }
  }

  return (
    <Styled className="report-preview-component">
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            reportStatus: 1,
          }}
          onFinish={(values) => {}}
          onValuesChange={(changedValues, allValues) => {
            const calculationField = _.find(calculationFields, (calculationField) => {
              return _.some(changedValues, (changedValue, code) => {
                return _.includes(calculationField.cal_equation, `{${code}}`)
              })
            })

            if (calculationField) {
              if (calculationField?.cal_equation) {
                let str = calculationField?.cal_equation

                const codes = str.match(/{([^}]+)}/g)

                _.forEach(codes, (code) => {
                  const data = allValues?.[code.slice(1,-1)] || 0
                  str = _.replace(str, code, data)
                })

                const value = evil(str)

                form.setFieldsValue({
                  [calculationField.report_type_attribute_code]: value,
                })
              }
            }
          }}
        >
          <Form.Item label={t('Report Status')} name="reportStatus">
            <Select
              showSearch
              options={_.map(reportStatuses, (reportStatus) => {
                return {
                  label: reportStatus?.name,
                  value: reportStatus?.id,
                }
              })}
              filterOption={(input, option) => {
                return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }}
            />
          </Form.Item>

          <Form.Item
            shouldUpdate={(prevValues, curValues) =>
              prevValues.reportStatus !== curValues.reportStatus
            }
          >
            {({ getFieldValue }) => {
              const currentReportStatus = getFieldValue('reportStatus')

              let filterReportTypeAttributes = reportTypeAttributes

              if (currentReportStatus) {
                filterReportTypeAttributes = _.filter(reportTypeAttributes, {
                  report_status_id: currentReportStatus,
                })
              }

              const groupReportTypeAttributes = _.groupBy(
                filterReportTypeAttributes,
                'attribute_group_id'
              )

              const orderGroupAttributes = _.orderBy(groupAttributes, ['order'], ['asc'])

              const orderGroupReportTypeAttributes = []

              _.forEach(orderGroupAttributes, (groupAttribute) => {
                const reportTypeAttributes = _.find(
                  groupReportTypeAttributes,
                  (reportTypeAttributes, attributeGroupId) => {
                    return groupAttribute.id === Number(attributeGroupId)
                  }
                )
                if (reportTypeAttributes) {
                  orderGroupReportTypeAttributes.push({
                    ...groupAttribute,
                    reportTypeAttributes,
                  })
                }
              })

              return (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Typography.Text>{t('Report Header Attribute')}</Typography.Text>
                    <Card disabled bodyStyle={{ backgroundColor: '#DFDFDF' }}></Card>
                  </div>

                  {_.map(orderGroupReportTypeAttributes, (orderGroupReportTypeAttribute, index) => {
                    const groupAttribute = _.find(groupAttributes, {
                      id: orderGroupReportTypeAttribute.id,
                    })
                    const reportTypeAttributes = orderGroupReportTypeAttribute?.reportTypeAttributes
                    const groupLines = _.groupBy(reportTypeAttributes, 'report_line')
                    return (
                      <div key={index}>
                        <Typography.Text>{groupAttribute?.name}</Typography.Text>
                        <Card>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            {_.map(groupLines, (groupLineReportTypeAttributes, line) => {
                              const span = 24 / _.size(groupLineReportTypeAttributes)
                              const orderReportTypeAttributes = _.orderBy(
                                groupLineReportTypeAttributes,
                                ['report_order'],
                                ['asc']
                              )
                              return (
                                <Row key={line} gutter={8}>
                                  {_.map(
                                    orderReportTypeAttributes,
                                    (reportTypeAttribute, index) => {
                                      return (
                                        <Col key={index} span={span}>
                                          {renderInputByType({ record: reportTypeAttribute })}
                                        </Col>
                                      )
                                    }
                                  )}
                                </Row>
                              )
                            })}
                          </Space>
                        </Card>
                      </div>
                    )
                  })}
                </Space>
              )
            }}
          </Form.Item>
        </Form>
      </Spin>
    </Styled>
  )
}
